import React from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { useStripe, useElements } from '@stripe/react-stripe-js';

const plans = {
  basic: {
    name: 'Basic',
    monthlyPrice: 29,
    yearlyPrice: 290,
  },
  professional: {
    name: 'Professional',
    monthlyPrice: 49,
    yearlyPrice: 490,
  },
  enterprise: {
    name: 'Enterprise',
    monthlyPrice: 99,
    yearlyPrice: 990,
  },
};

function ReviewDetails({ data, onSubmit, onBack }) {
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const { user } = useSelector((state) => state.auth);
  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async () => {
    if (!stripe || !elements) {
      console.error('Stripe not initialized');
      return;
    }
    
    setIsSubmitting(true);
    try {
      await onSubmit(stripe, elements);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const selectedPlan = plans[data.subscription.plan];
  const price = data.subscription.interval === 'month' 
    ? selectedPlan.monthlyPrice 
    : selectedPlan.yearlyPrice;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Review Your Information
      </Typography>

      <Grid container spacing={3}>
        {/* Account Details */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom color="primary">
              Account Details
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="Account Name"
                  secondary={data.accountDetails.name}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Account Type"
                  secondary={data.accountDetails.type.charAt(0).toUpperCase() + 
                           data.accountDetails.type.slice(1)}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Account Owner"
                  secondary={user.name}
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Addresses */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom color="primary">
              Billing Address
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="Street"
                  secondary={data.addresses.billing.street}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="City"
                  secondary={data.addresses.billing.city}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="State"
                  secondary={data.addresses.billing.state}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="ZIP Code"
                  secondary={data.addresses.billing.zipCode}
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom color="primary">
              Shipping Address
            </Typography>
            {data.addresses.shipping.sameAsBilling ? (
              <Typography color="text.secondary">
                Same as billing address
              </Typography>
            ) : (
              <List dense>
                <ListItem>
                  <ListItemText
                    primary="Street"
                    secondary={data.addresses.shipping.street}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="City"
                    secondary={data.addresses.shipping.city}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="State"
                    secondary={data.addresses.shipping.state}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="ZIP Code"
                    secondary={data.addresses.shipping.zipCode}
                  />
                </ListItem>
              </List>
            )}
          </Paper>
        </Grid>

        {/* Subscription Details */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom color="primary">
              Subscription Details
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="Selected Plan"
                  secondary={selectedPlan.name}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Billing Interval"
                  secondary={data.subscription.interval === 'month' ? 'Monthly' : 'Yearly'}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Amount"
                  secondary={`${formatCurrency(price)}/${data.subscription.interval}`}
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Payment Method */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom color="primary">
              Payment Method
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="Card"
                  secondary={`${data.paymentMethod.brand.toUpperCase()} •••• ${data.paymentMethod.last4}`}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Expiration"
                  secondary={`${data.paymentMethod.expMonth}/${data.paymentMethod.expYear}`}
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>
      </Grid>

      <Divider sx={{ my: 4 }} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          onClick={onBack}
          disabled={isSubmitting}
        >
          Back
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <CircularProgress size={24} sx={{ mr: 1 }} />
              Creating Account...
            </>
          ) : (
            'Create Account'
          )}
        </Button>
      </Box>
    </Box>
  );
}

export default ReviewDetails; 